import { useEffect, useRef, useState } from "react";
import { LifeBuoy, Send, Loader2, RotateCcw, Mail, Bot, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API, notifyErr } from "@/lib/api";

const strip = (t) => (t || "").replace("[[SUPPORTO]]", "").trim();

async function streamChat(message, onDelta) {
  const headers = { "Content-Type": "application/json" };
  const cid = localStorage.getItem("company_id");
  if (cid) headers["X-Company-Id"] = cid;
  const r = await fetch(`${API}/support/chat`, { method: "POST", credentials: "include", headers, body: JSON.stringify({ message }) });
  if (!r.ok) { let d = "Si è verificato un errore"; try { d = (await r.json()).detail || d; } catch (_) { /* no json */ } throw new Error(d); }
  const reader = r.body.getReader(), dec = new TextDecoder();
  let buf = "", final = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n"); buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.done) final = ev; else onDelta(ev.delta);
    }
  }
  return final;
}

function Bubble({ m, onEscalate }) {
  const me = m.role === "user";
  return (
    <div className={`flex gap-3 ${me ? "justify-end" : ""}`} data-testid={`support-msg-${m.role}`}>
      {!me && <span className="w-8 h-8 shrink-0 rounded-full bg-brand/15 text-brand grid place-items-center"><Bot size={16} /></span>}
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${me ? "bg-brand text-white rounded-br-sm" : "bg-zinc-900 border border-zinc-800 rounded-bl-sm"}`}>
        {strip(m.content) || <Loader2 size={16} className="animate-spin text-zinc-500" />}
        {m.escalate && m.id && <div className="mt-3 pt-3 border-t border-zinc-800">
          <Button size="sm" variant="outline" disabled={!!m.escalated_at} onClick={() => onEscalate(m)} data-testid={`support-escalate-${m.id}`}>
            <Mail size={14} className="mr-1" />{m.escalated_at ? "Inviata al supporto" : "Scrivi al supporto NOLIUM"}</Button></div>}
      </div>
      {me && <span className="w-8 h-8 shrink-0 rounded-full bg-zinc-800 grid place-items-center"><User size={16} /></span>}
    </div>
  );
}

export default function Support() {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();
  useEffect(() => { api.get("/support/chat").then((r) => setMsgs(r.data)).catch((e) => { notifyErr(e); setMsgs([]); }); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((s) => [...s, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const patch = (fn) => setMsgs((s) => { const c = [...s]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const fin = await streamChat(message, (d) => patch((m) => ({ ...m, content: m.content + d })));
      if (fin) patch((m) => ({ ...m, id: fin.id, content: fin.content, escalate: fin.escalate }));
    } catch (e) { toast.error(e.message); setMsgs((s) => s.slice(0, -2)); setText(message); }
    setBusy(false);
  };
  const escalate = async (m) => {
    try { const r = await api.post("/support/escalate", { message_id: m.id }); toast.success(r.data.message); setMsgs((s) => s.map((x) => (x.id === m.id ? { ...x, escalated_at: "now" } : x))); }
    catch (e) { notifyErr(e); }
  };
  const reset = async () => { try { await api.delete("/support/chat"); setMsgs([]); } catch (e) { notifyErr(e); } };
  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-4xl" data-testid="support-page">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div><h1 className="text-4xl font-bold text-white flex items-center gap-3"><LifeBuoy className="text-brand" />Assistenza</h1>
          <p className="text-sm text-zinc-400 mt-1">Se hai bisogno di indicazioni su come usare il tuo CRM, chiedi pure qui in chat! L'assistente AI ti risponderà in tempo reale.</p></div>
        {!!msgs?.length && <Button variant="outline" size="sm" onClick={reset} disabled={busy} data-testid="support-reset-btn"><RotateCcw size={14} className="mr-1" />Nuova chat</Button>}
      </div>
      <div className="flex-1 overflow-y-auto space-y-4 rounded-xl border border-zinc-800 bg-zinc-950/50 p-4" data-testid="support-messages">
        {msgs === null ? <Loader2 className="animate-spin text-zinc-500" /> : !msgs.length ? (
          <div className="h-full grid place-items-center text-center"><div><Bot size={32} className="mx-auto text-brand mb-3" />
            <p className="text-zinc-400 text-sm">Ciao! Come posso aiutarti con NOLIUM?</p></div></div>
        ) : msgs.map((m, i) => <Bubble key={m.id || i} m={m} onEscalate={escalate} />)}
        <div ref={end} />
      </div>
      <div className="mt-3 flex gap-2 items-end">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000} placeholder="Scrivi la tua domanda…" className="resize-none"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="support-input" />
        <Button className="btn-brand h-[60px] px-5" disabled={busy || !text.trim()} onClick={() => send()} data-testid="support-send-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</Button>
      </div>
    </div>
  );
}
